import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import generateQueryKey from '../helpers/generate-query-key';
import { generateUrl } from '../../helpers/request-builder';
import { CubedConfigResource, CubedResource } from '../../types';

type RequestResponseData = { key: string; value: string | number | boolean };

type UseDeleteResourceWithPayloadArgs = {
    resource: CubedResource | CubedConfigResource;
    handleOnSuccess?: () => void;
    handleOnError?: () => void;
};

const useDeleteResourceWithPayload = ({
    resource,
    handleOnSuccess,
    handleOnError,
}: UseDeleteResourceWithPayloadArgs) => {
    const queryClient = useQueryClient();
    const queryKey = generateQueryKey({ resource });

    const mutation = useMutation({
        mutationFn: async (payload: RequestResponseData[][]) => {
            const requests = payload.map((resourceData: RequestResponseData[]) => {
                const url = generateUrl(resource.category, resource.id, resourceData);

                if (url) {
                    return axios({
                        method: 'DELETE',
                        url: url,
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }).catch(error => {
                        throw new Error(error.response.data.message);
                    });
                }
                throw new Error('Error when generating url');
            });

            await Promise.all(requests);
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: queryKey,
                })
                .then(() => {
                    handleOnSuccess && handleOnSuccess();
                });
        },
        onError: handleOnError,
    });

    return mutation;
};

export default useDeleteResourceWithPayload;
